import { AdminGroups, AdminUsers, NavItemRef } from 'Interfaces/Admin';
import { collectGroupUsers, findParentOrganization } from 'reducers/admin/selectors';

/**
 *  The rules that decide whether dragging a user or a group onto another group
 *  is allowed. Ported from revertDragged in WebContent/assets/admin/js/admin.js.
 *
 *  The guard exists so an organization cannot be pushed past the seat count it
 *  paid for (`orgMaxUsers`). Moves that stay inside the same organization are
 *  exempt, because they do not change that organization's head count.
 */

export interface ReparentCheck {
  allowed: boolean;
  /** Name of the organization that is full; set only when blocked. */
  blockedBy?: string;
}

const ALLOWED: ReparentCheck = { allowed: true };

/** The organization a group belongs to: itself when it is one, else its nearest ancestor. */
const organizationOf = (groups: AdminGroups, groupId: number) => {
  const group = groups.find((g) => g.id === groupId);
  if (!group) {
    return undefined;
  }
  return group.organization ? group : findParentOrganization(groups, group.parentId);
};

const hasRoom = (
  groups: AdminGroups, users: AdminUsers, organizationId: number, limit: number | null, incoming: number,
): boolean => {
  if (!limit || limit <= 0) {
    return true;
  }
  const current = collectGroupUsers(users, groups, organizationId, true, true).length;
  return current < limit && current + incoming < limit;
};

export const canMoveUser = (
  groups: AdminGroups, users: AdminUsers, userId: number, targetGroupId: number,
): ReparentCheck => {
  const user = users.find((u) => u.id === userId);
  const target = groups.find((g) => g.id === targetGroupId);
  if (!user || !target) {
    return ALLOWED;
  }

  const organization = organizationOf(groups, targetGroupId);
  if (!organization) {
    return ALLOWED;
  }

  // Moving inside the same organization leaves its head count unchanged.
  const currentOrganization = findParentOrganization(groups, user.groupId)
    || organizationOf(groups, user.groupId);
  if (currentOrganization && currentOrganization.id === organization.id) {
    return ALLOWED;
  }

  return hasRoom(groups, users, organization.id, organization.orgMaxUsers, 0)
    ? ALLOWED
    : { allowed: false, blockedBy: target.name };
};

export const canMoveGroup = (
  groups: AdminGroups, users: AdminUsers, groupId: number, targetGroupId: number,
): ReparentCheck => {
  const source = groups.find((g) => g.id === groupId);
  const target = groups.find((g) => g.id === targetGroupId);
  if (!source || !target) {
    return ALLOWED;
  }

  // A group cannot be moved into itself or into one of its own descendants.
  if (groupId === targetGroupId || isDescendantOf(groups, targetGroupId, groupId)) {
    return { allowed: false, blockedBy: target.name };
  }

  /**
   *  NOTE: the legacy code read the *source* group's organization flag when
   *  deciding whether the target was itself an organization, so dropping a
   *  plain group onto an organization skipped the limit check entirely. That
   *  looks like a slip rather than a rule, so the target is used here.
   */
  const organization = organizationOf(groups, targetGroupId);
  if (!organization) {
    return ALLOWED;
  }

  const sourceOrganization = findParentOrganization(groups, source.parentId)
    || organizationOf(groups, groupId);
  if (sourceOrganization && sourceOrganization.id === organization.id) {
    return ALLOWED;
  }

  const incoming = collectGroupUsers(users, groups, groupId, true, true).length;
  return hasRoom(groups, users, organization.id, organization.orgMaxUsers, incoming)
    ? ALLOWED
    : { allowed: false, blockedBy: target.name };
};

/** True when `groupId` sits somewhere beneath `ancestorId`. */
export const isDescendantOf = (groups: AdminGroups, groupId: number, ancestorId: number): boolean => {
  const seen = new Set<number>();
  let current = groups.find((g) => g.id === groupId);
  while (current && !seen.has(current.id)) {
    if (current.parentId === ancestorId) {
      return true;
    }
    seen.add(current.id);
    current = groups.find((g) => g.id === current?.parentId);
  }
  return false;
};

export const canReparent = (
  groups: AdminGroups, users: AdminUsers, source: NavItemRef, targetGroupId: number,
): ReparentCheck =>
  source.type === 'user'
    ? canMoveUser(groups, users, source.id, targetGroupId)
    : canMoveGroup(groups, users, source.id, targetGroupId);

/** Nothing to do when the item is already where it was dropped. */
export const isNoOpMove = (
  groups: AdminGroups, users: AdminUsers, source: NavItemRef, targetGroupId: number,
): boolean => {
  if (source.type === 'user') {
    return users.find((u) => u.id === source.id)?.groupId === targetGroupId;
  }
  return groups.find((g) => g.id === source.id)?.parentId === targetGroupId;
};
